import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { NotificationDto } from "./types";
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "./api";

type UseNotificationsOptions = {
  accessToken?: string | null;
};

function getErrorStatus(err: unknown) {
  return Number((err as any)?.response?.status ?? (err as any)?.status ?? 0);
}

function isGracefulNotificationsState(err: unknown) {
  const status = getErrorStatus(err);
  if (status === 401 || status === 403 || status === 404) return true;

  const msg =
    err instanceof Error ? err.message : typeof err === "string" ? err : "";

  return msg.includes("401") || msg.includes("403") || msg.includes("404");
}

export function useNotifications({ accessToken }: UseNotificationsOptions) {
  const [notifications, setNotifications] = useState<NotificationDto[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const isAuthenticated = !!accessToken;

  const loadPage = useCallback(
    async (nextPage: number, replace: boolean) => {
      if (!accessToken) return;

      if (abortRef.current) {
        abortRef.current.abort();
      }

      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      setError(null);

      try {
        const res = await fetchNotifications(
          { page: nextPage, pageSize, signal: controller.signal },
          accessToken,
        );

        if (controller.signal.aborted) return;

        setUnreadCount(res.unreadCount);
        setHasMore(res.page < res.totalPages);
        setNotifications((prev) =>
          replace ? res.items : [...prev, ...res.items],
        );
        setPage(res.page);
        setInitialLoaded(true);
      } catch (err: any) {
        if (err?.name === "CanceledError" || err?.name === "AbortError") return;

        // 401/403/404 = guest mode o endpoint no disponible todavía
        if (isGracefulNotificationsState(err)) {
          setNotifications([]);
          setUnreadCount(0);
          setHasMore(false);
          setInitialLoaded(true);
          setError(null);
          return;
        }

        console.error("[notifications] load error", err);
        setError(err?.message || "Error al cargar notificaciones");
      } finally {
        setLoading(false);
      }
    },
    [accessToken, pageSize],
  );

  const reload = useCallback(() => {
    if (!accessToken) return;
    loadPage(1, true);
  }, [accessToken, loadPage]);

  const loadMore = useCallback(() => {
    if (!accessToken || loading || !hasMore) return;
    loadPage(page + 1, false);
  }, [accessToken, loading, hasMore, page, loadPage]);

  const markAsRead = useCallback(
    async (id: string) => {
      if (!accessToken) return;

      setNotifications((prev) =>
        prev.map((n) =>
          n.id === id && !n.readAt
            ? { ...n, readAt: new Date().toISOString() }
            : n,
        ),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));

      try {
        await markNotificationRead(id, accessToken);
      } catch (err) {
        if (!isGracefulNotificationsState(err)) {
          console.error("[notifications] mark read error", err);
        }
        reload();
      }
    },
    [accessToken, reload],
  );

  const markAllAsRead = useCallback(async () => {
    if (!accessToken) return;

    setNotifications((prev) =>
      prev.map((n) =>
        n.readAt ? n : { ...n, readAt: new Date().toISOString() },
      ),
    );
    setUnreadCount(0);

    try {
      await markAllNotificationsRead(accessToken);
    } catch (err) {
      if (!isGracefulNotificationsState(err)) {
        console.error("[notifications] mark all read error", err);
      }
      reload();
    }
  }, [accessToken, reload]);

  useEffect(() => {
    if (!isAuthenticated) {
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      setNotifications([]);
      setUnreadCount(0);
      setHasMore(false);
      setInitialLoaded(false);
      setError(null);
      return;
    }

    loadPage(1, true);

    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };
  }, [isAuthenticated, loadPage]);

  const hasUnread = useMemo(() => unreadCount > 0, [unreadCount]);

  return {
    notifications,
    unreadCount,
    hasUnread,
    loading,
    error,
    initialLoaded,
    hasMore,
    page,
    pageSize,
    reload,
    loadMore,
    markAsRead,
    markAllAsRead,
  };
}